import { useCallback, useEffect, useReducer, Reducer } from 'react';
import { treeToMap, CollectionOption, CollectionGroup, Hashmap } from './treeToMap';
const initialState = {
  allowFilter: true,
  checkedKeys: [],
  checkedEvent: {
    type: 'init',
    key: ''
  },
  collectionGroups: {},
  collectionOptions: new Map(),
  groupsKeys: {},
  interactiveKeys: [],
  isMultiselectable: false,
  length: 0,
  onClick: null,
  onKeyDown: null,
  optionKeys: [],
  selectedKey: ''
};
const reducer = (state, action) => {
  let newState = state;
  switch (action.type) {
    case 'updateSelectedKeys':
      let selectedKey = state.selectedKey;
      if (action.payload.event.type === 'click') {
        selectedKey = action.payload.id;
      }
      if (action.payload.event.type === 'keydown') {
        if (action.payload.event.key === 'ArrowDown') {
          const currentIndex = state.interactiveKeys.findIndex(key => key === state.selectedKey);
          selectedKey = state.interactiveKeys[Math.min(currentIndex + 1, state.interactiveKeys.length - 1)];
        }
        if (action.payload.event.key === 'ArrowUp') {
          const currentIndex = state.interactiveKeys.findIndex(key => key === state.selectedKey);
          selectedKey = state.interactiveKeys[Math.max(currentIndex - 1, 0)];
        }
        if (action.payload.event.key === 'Home') {
          selectedKey = state.interactiveKeys[0];
        }
        if (action.payload.event.key === 'End') {
          selectedKey = state.interactiveKeys[state.interactiveKeys.length - 1];
        }
      }
      newState = {
        ...state,
        selectedKey
      };
      break;
    case 'updateCheckedKeys':
      let checkedKeys = state.checkedKeys;
      let checkedEvent = state.checkedEvent;
      if (action.payload.event?.type === 'click') {
        if (state.checkedKeys.includes(action.payload.id)) {
          checkedKeys = state.checkedKeys.filter(key => key !== action.payload.id);
          checkedEvent = {
            type: 'remove',
            key: action.payload.id
          };
        } else {
          checkedKeys = state.isMultiselectable ? [...state.checkedKeys, action.payload.id] : [action.payload.id];
          checkedEvent = {
            type: 'add',
            key: action.payload.id
          };
        }
      }
      if (action.payload.event?.type === 'keydown' && [' ', 'Enter'].includes(action.payload.event.key) && state.interactiveKeys.find(key => key === state.selectedKey) !== undefined) {
        action.payload.event.preventDefault();
        if (state.checkedKeys.includes(state.selectedKey)) {
          checkedKeys = state.checkedKeys.filter(key => key !== state.selectedKey);
          checkedEvent = {
            type: 'remove',
            key: state.selectedKey
          };
        } else {
          checkedKeys = state.isMultiselectable ? [...state.checkedKeys, state.selectedKey] : [state.selectedKey];
          checkedEvent = {
            type: 'add',
            key: state.selectedKey
          };
        }
      }
      if (action.payload.checkedOptions) {
        checkedKeys = action.payload.checkedOptions;
        checkedEvent = {
          type: 'init',
          key: action.payload.checkedOptions[0]
        };
      }
      newState = {
        ...state,
        checkedKeys,
        checkedEvent
      };
      break;
    case 'updateIsMultiselectable':
      newState = {
        ...state,
        isMultiselectable: action.payload
      };
      break;
    case 'filterKeys':
      {
        if (state.collectionOptions.size === 0 || !state.allowFilter || action.payload.query === '') {
          return newState = state;
        }
        let interactiveKeys = [];
        let posinset = 0;
        let matchIndices = [];
        for (const [key, value] of state.collectionOptions.entries()) {
          const isVisible = action.payload.filterOptions(value, action.payload.query);
          if (isVisible && !value.disabled) {
            interactiveKeys.push(key);
          }
          if (isVisible) {
            posinset = posinset + 1;
            matchIndices = [0, action.payload.query.length];
          }
          state.collectionOptions.set(key, {
            ...value,
            isVisible,
            posinset,
            matchIndices
          });
        }
        newState = {
          ...state,
          interactiveKeys,
          length: posinset
        };
      }
      break;
    case 'updateCollection':
      if (!action.payload) return state;
      const initial = {
        interactiveKeys: [],
        optionKeys: [],
        groupsKeys: {},
        collectionOptions: new Map(),
        collectionGroups: {},
        childIndex: 0
      };
      const {
        collectionOptions,
        collectionGroups,
        groupsKeys,
        interactiveKeys,
        optionKeys
      } = treeToMap(action.payload, initial);
      newState = {
        ...state,
        collectionOptions,
        collectionGroups,
        groupsKeys,
        length: optionKeys?.length || 0,
        interactiveKeys,
        optionKeys
      };
      break;
    case 'updateAllowFilter':
      newState = {
        ...state,
        allowFilter: action.payload
      };
      break;
    case 'resetFilter':
      {
        if (state.collectionOptions.size === undefined) return newState = state;
        let interactiveKeys = [];
        let posinset = 0;
        for (const [key, value] of state.collectionOptions.entries()) {
          if (!value.disabled) {
            interactiveKeys.push(key);
          }
          posinset = posinset + 1;
          state.collectionOptions.set(key, {
            ...value,
            isVisible: true,
            posinset,
            matchIndices: []
          });
        }
        newState = {
          ...state,
          interactiveKeys,
          length: state.collectionOptions.size
        };
      }
      break;
    default:
      return state;
  }
  return newState;
};
const initialfilterOptions = (item, query) => {
  if (!query || !item.label) return false;
  const labelRegExp = new RegExp(`^${query}`, 'i');
  return labelRegExp.test(item.label);
};
const emptyArray = [];
export function useListboxState({
  allowFilter = true,
  checkedOptions = emptyArray,
  filterOptions = initialfilterOptions,
  isMultiselectable = false,
  options = emptyArray,
  query = ''
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    dispatch({
      type: 'updateIsMultiselectable',
      payload: isMultiselectable
    });
  }, [isMultiselectable]);
  useEffect(() => {
    dispatch({
      type: 'updateAllowFilter',
      payload: allowFilter
    });
    if (!allowFilter) {
      dispatch({
        type: 'resetFilter',
        payload: undefined
      });
    }
  }, [allowFilter]);
  useEffect(() => {
    if (!Array.isArray(options)) return;
    dispatch({
      type: 'updateCollection',
      payload: options
    });
  }, [options]);
  useEffect(() => {
    dispatch({
      type: 'filterKeys',
      payload: {
        query,
        filterOptions
      }
    });
    if (query === '') {
      dispatch({
        type: 'resetFilter',
        payload: undefined
      });
    }
  }, [query, filterOptions]);
  useEffect(() => {
    dispatch({
      type: 'updateCheckedKeys',
      payload: {
        checkedOptions
      }
    });
  }, [checkedOptions]);
  const handleClick = useCallback((e, id) => {
    dispatch({
      type: 'updateSelectedKeys',
      payload: {
        event: e,
        id
      }
    });
    dispatch({
      type: 'updateCheckedKeys',
      payload: {
        event: e,
        id
      }
    });
  }, []);
  const handleKeyDown = useCallback(event => {
    dispatch({
      type: 'updateSelectedKeys',
      payload: {
        event
      }
    });
    dispatch({
      type: 'updateCheckedKeys',
      payload: {
        event
      }
    });
  }, []);
  const removeCheckedKey = useCallback(id => {
    dispatch({
      type: 'updateCheckedKeys',
      payload: {
        id,
        event: {
          type: 'click'
        }
      }
    });
  }, []);
  return {
    checkedEvent: state.checkedEvent,
    checkedKeys: state.checkedKeys,
    collectionGroups: state.collectionGroups,
    collectionOptions: state.collectionOptions,
    groupsKeys: state.groupsKeys,
    interactiveKeys: state.interactiveKeys,
    isMultiselectable: state.isMultiselectable,
    length: state.length,
    onChange: handleClick,
    onKeyDown: handleKeyDown,
    optionKeys: state.optionKeys,
    removeCheckedKey,
    selectedKey: state.selectedKey
  };
}